import React, { FC, useState } from 'react';
import dynamic from 'next/dynamic';
import {
  useTranslation,
  useHttpClient,
  useExperiments,
} from '@wix/fe-essentials-standalone';
import { Box, TextButton, Text } from '@wix/design-system';
import { ChevronDown, ChevronUp, FlagSmall } from '@wix/wix-ui-icons-common';
import GoodToKnow from '../GoodToKnow';
import SingleUseLicense from '../SingleUseLicense';
import { useTemplateBi } from '@components/Hooks/useTemplateBi';
import { useHeaderContext } from '@components/Contexts/HeaderContext';
import { ReportTemplateFormFields } from '@components/Template/AdditionalInfo/ReportTemplateModal/ReportTemplateModal';
import ReportTemplateModalSuccessMsg from '../ReportTemplateModalSuccessMsg';
import { ENABLE_REPORT_TEMPLATE } from '@utils/constants';
import ReportTemplateErrorNotification from '../ReportTemplateModal/ReportTemplateErrorNotification';

type AdditionalInfoDesktopProps = {
  hasAdditionalPurchaseApps: boolean;
  templateName: string;
  templateListingId: string;
};

const ADDITIONAL_INFO_KEYS = {
  SINGLE_USE_LICENSE: 'singleUseLicense',
  GOOD_TO_KNOW: 'goodToKnow',
} as const;

const DynamicReportTemplateModal = dynamic(
  () => import('../ReportTemplateModal'),
  {
    loading: () => null,
    ssr: false,
  },
);

const AdditionalInfoDesktop: FC<AdditionalInfoDesktopProps> = ({
  hasAdditionalPurchaseApps,
  templateName,
  templateListingId,
}) => {
  const httpClient = useHttpClient();
  const { experiments } = useExperiments({ readOnly: true });
  const showReportTemplateEnable = experiments.enabled(ENABLE_REPORT_TEMPLATE);
  const { userDetails } = useHeaderContext();
  const { logBiClick } = useTemplateBi({ is_mobile: false });

  const [openReportTemplateModal, setOpenReportTemplateModal] = useState(false);
  const [openSingleUseLicense, setOpenSingleUseLicense] = useState(false);
  const [openGoodToKnow, setOpenGoodToKnow] = useState(false);
  const [showReportTemplateSuccessMsg, setShowReportTemplateSuccessMsg] =
    useState(false);
  const [showReportTemplateError, setShowReportTemplateError] = useState(false);
  const [reportFormValues, setReportFormValues] =
    useState<ReportTemplateFormFields>({
      issueType: '',
      issueDescription: '',
      agreeToBeContacted: false,
    });

  const handleToggleCollapse = (
    key: (typeof ADDITIONAL_INFO_KEYS)[keyof typeof ADDITIONAL_INFO_KEYS],
  ) => {
    switch (key) {
      case ADDITIONAL_INFO_KEYS.SINGLE_USE_LICENSE:
        setOpenSingleUseLicense((prev) => !prev);
        break;
      case ADDITIONAL_INFO_KEYS.GOOD_TO_KNOW:
        setOpenGoodToKnow((prev) => !prev);
        break;
      default:
        break;
    }
  };

  const resetFormValues = () => {
    setReportFormValues({
      issueType: '',
      issueDescription: '',
      agreeToBeContacted: false,
    });
  };

  const onClose = (clickType: string) => {
    setOpenReportTemplateModal(false);
    resetFormValues();
    logBiClick({
      clickType,
    });
  };

  const onSubmit = async (data?: ReportTemplateFormFields) => {
    if (data) {
      setReportFormValues(data);
    }
    const values = data || reportFormValues;
    logBiClick({
      clickType: 'Report template modal - Submit report',
      click_data: {
        type_of_issue: values?.issueType,
        describe_issue: values?.issueDescription,
        agree_to_be_contact: values?.agreeToBeContacted,
      },
    });
    resetFormValues();
    try {
      setShowReportTemplateError(false);
      await httpClient.post('/api/template/reportTemplate', {
        templateListingId,
        reportText: values.issueDescription,
      });
      setOpenReportTemplateModal(false);
      setShowReportTemplateSuccessMsg(true);
    } catch (error) {
      setShowReportTemplateError(true);
      setOpenReportTemplateModal(false);
    }
  };

  const { t } = useTranslation();
  return (
    <Box
      gap="18px"
      direction="vertical"
      align="left"
      dataHook="additional-info-box"
    >
      <Box direction="vertical" gap="6px">
        <TextButton
          aria-expanded={openSingleUseLicense}
          size="small"
          dataHook="single-use-license-cta"
          skin="dark"
          onClick={() => {
            !openSingleUseLicense &&
              logBiClick({ clickType: 'Single use license' });
            handleToggleCollapse(ADDITIONAL_INFO_KEYS.SINGLE_USE_LICENSE);
          }}
          suffixIcon={openSingleUseLicense ? <ChevronUp /> : <ChevronDown />}
        >
          {t('marketplace.template-page.single-use-license-title')}
        </TextButton>
        {openSingleUseLicense && (
          <Box maxWidth="299px">
            <SingleUseLicense isMobileView={false} textSize="tiny" />
          </Box>
        )}
      </Box>
      <Box direction="vertical" display="flex">
        <TextButton
          aria-expanded={openGoodToKnow}
          size="small"
          dataHook="good-to-know-cta"
          skin="dark"
          onClick={() => {
            !openGoodToKnow && logBiClick({ clickType: 'good to know' });
            handleToggleCollapse(ADDITIONAL_INFO_KEYS.GOOD_TO_KNOW);
          }}
          suffixIcon={openGoodToKnow ? <ChevronUp /> : <ChevronDown />}
        >
          {t('marketplace.template-page.good-to-know-title')}
        </TextButton>
        {openGoodToKnow && (
          <Box
            maxWidth="299px"
            gap="15px"
            direction="vertical"
            paddingTop="6px"
          >
            <GoodToKnow
              textSize="tiny"
              hasAdditionalPurchaseApps={hasAdditionalPurchaseApps}
            />
          </Box>
        )}
      </Box>
      {userDetails?.email && showReportTemplateEnable && (
        <TextButton
          dataHook="report-template-cta"
          skin="dark"
          onClick={() => {
            setOpenReportTemplateModal(true);
            logBiClick({ clickType: 'Report this template' });
          }}
        >
          <Box verticalAlign="middle" gap="6px">
            <FlagSmall color="#868AA5" />
            <Text size="tiny" secondary>
              {t('marketplace.template-page.report-template-cta')}
            </Text>
          </Box>
        </TextButton>
      )}
      {showReportTemplateSuccessMsg && (
        <ReportTemplateModalSuccessMsg
          isOpen={showReportTemplateSuccessMsg}
          onClose={() => setShowReportTemplateSuccessMsg(false)}
        />
      )}
      {openReportTemplateModal && (
        <DynamicReportTemplateModal
          onSubmit={onSubmit}
          isOpen={openReportTemplateModal}
          onClose={onClose}
          templateName={templateName}
        />
      )}
      <ReportTemplateErrorNotification
        onSubmit={onSubmit}
        setShowReportTemplateError={setShowReportTemplateError}
        showReportTemplateError={showReportTemplateError}
      />
    </Box>
  );
};

export default AdditionalInfoDesktop;
